<template>
  <div class="detail-dialog">
    <div class="detail-dialog-header">
      <div class="detail-dialog-header-title">
        {{ title }}
      </div>
      <div class="detail-dialog-header-button">
        <v-btn
          class="ml-2"
          small
          outlined
          color="primary"
          @click.stop="$emit('close')"
        >
          <v-icon
            small
          >
            mdi-close-box-outline
          </v-icon>
          닫기
        </v-btn>
      </div>
    </div>
    <div class="custom-spacer" />
    <cms-plan-sub-item
      v-for="(item, idx) in editContext"
      :key="`${item.rentalcompanyId}-${item.cmsId}`"
      :value="editContext[idx]"
      :idx="idx"
      :cms-bank-codes="cmsBankCodes"
      @save-member="onSaveMember"
      @refresh-member="onRefreshMember"
      @cancel-member="onCancelMember"
    />
    <div class="custom-spacer" />
    <div class="detail-dialog-footer">
      <div class="d-flex justify-center">
        <v-btn
          v-if="isAddPossible"
          small
          color="primary"
          @click.stop="addCmsMember()"
        >
          <v-icon
            small
          >
            mdi-plus
          </v-icon>
          CMS 추가
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import time from "../../assets/plugins/mps-common/time";
import {cloneDeep} from "lodash";
import {PATH} from "@/constants";
import {DateTime} from "luxon";
import CmsPlanSubItem from "./CmsMemberSubItem.vue";
import AdmCmsMember from "@/model/admCmsMember";

export default {
  name: "CmsPlanDetail",
  components: {CmsPlanSubItem},
  props: {
    item: {
      type: Object,
      default: undefined
    },

    title: {
      type: String,
      default: 'CMS',
    },

    cmsBankCodes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      context: [],
      editContext: []
    }
  },
  computed: {
    cmsMemberIdList() {
      if (!this.item || !this.item.cmsMemberIds || this.item.cmsMemberIds === '')
        return []

      const toStrIds = String(this.item.cmsMemberIds)

      return toStrIds.split(',')
    },

    isAddPossible() {
      const findNewMember = this.editContext.find(e => !e.cmsId)
      return !findNewMember
    },
  },
  watch: {
    item: {
      immediate: true,
      deep: true,
      handler(newVal) {
      }
    },

    context: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.editContext = cloneDeep(newVal)
      }
    },

    cmsMemberIdList: {
      immediate: true,
      deep: true,
      async handler(newVal) {
        if (!newVal || newVal.length <= 0)
          return this.addCmsMember()
        const reqArr = []
        newVal.forEach(e => {
          reqArr.push(this.loadCmsMember(e))
        })
        const resArr = await Promise.all(reqArr)

        this.context.push(...resArr.map((e) => this.convertCmsMember(e))
        )
      }
    },
  },

  methods: {
    loadCmsMember(cmsMemberId) {
      if (!cmsMemberId)
        return
      return this.$request(PATH.CMS_MEMBER.RETRIEVE)
        .setObject({
          cmsMemberId
        })
        .enqueue()
    },

    convertCmsMember(item) {
      if (!item) {
        return new AdmCmsMember().getNewMember(this.item.rentalcompanyId)
      } else {
        return new AdmCmsMember(item).getConvertMember()
      }
    },

    addCmsMember() {
      if (this.editContext.find(e => !e.cmsId)) {
        return this.$toasted.error('신규 등록은 1건만 가능합니다.')
      }

      this.editContext.push(this.convertCmsMember())
    },

    /**
     * 신규 CMS 등록 로직 (비대면 포함)
     */
    async onSaveMember(e) {
      const {internalValue, idx} = e
      let res
      if (internalValue.simpleYn === 'Y') { // 비대면
        res = await this.$request(PATH.CMS_MEMBER.SIMPLE_REGISTER)
          .setObject(internalValue)
          .enqueue()

      } else {
        if (internalValue.paymentKind === 'B' && !internalValue.agreeFile){
          return this.$toasted.error('동의서를 첨부해 주세요')
        }

        res = await this.$request(PATH.CMS_MEMBER.REGISTER)
          .setObject(internalValue)
          .enqueue()

        if (!!res && internalValue.paymentKind === 'B'){
          internalValue.cmsMemberId = res
          await this.onSaveAgreement(internalValue)
        }
      }

      if (!!res) {
        this.$toasted.success('신규 등록 성공')
        this.$emit('close')
        this.$emit('init')
      } else {
        this.$toasted.error('신규 등록 실패')
      }
    },

    /**
     * 동의서 파일 등록
     * @param internalValue
     * @return {Promise<boolean>}
     */
    async onSaveAgreement(internalValue) {
      const res = await this.$request(PATH.CMS_MEMBER.REGISTER_AGREEMENT)
        .appendPart('rentalcompanyId', internalValue.rentalcompanyId)
        .appendPart('cmsMemberId', internalValue.cmsMemberId)
        .appendFile('agreement', internalValue.agreeFile)
        .enqueue()

      return !!res
    },

    /**
     * CMS 동기화 로직 (비대면 포함)
     */
    async onRefreshMember(e) {
      const {internalValue, idx} = e
      const res = await this.$request(PATH.CMS_MEMBER.REFRESH)
        .setObject(internalValue)
        .enqueue()

      if (!!res) {
        this.editContext.splice(idx, 1, new AdmCmsMember(res).getConvertMember())
      }
    },

    /**
     * 신규 CMS 등록 취소
     * OR 등록된 CMS 해지 신청 (비대면 포함)
     */
    async onCancelMember(e) {
      if (!confirm('삭제하시겠습니까?')){
        return
      }

      const {internalValue, idx} = e
      if (!internalValue.cmsId) {
        this.editContext.splice(idx, 1)
        return this.$toasted.success('등록 취소처리 했습니다.')
      } else {
        const res = await this.$request(PATH.CMS_MEMBER.DELETE)
          .setObject(internalValue)
          .enqueue()
        if (!!res) {
          this.$toasted.success('삭제 성공')
          this.$emit('close')
          this.$emit('init')
        } else {
          this.$toasted.error('삭제 실패')
        }
      }
    },
  },
}
</script>

<style scoped>
.detail-dialog {
  background: #fff;
  padding: 12px 12px 12px 12px;
  width: 100%;
  min-width: 980px;
  max-width: 1000px;
}

.detail-dialog > div {
  padding: 4px 0;
}

.detail-dialog-header {
  position: sticky;
  top: 0;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  border-bottom: 2px solid #dddddd;
}

.detail-dialog-header-title {
  font-size: 18px;
  font-weight: bold;
  text-align: left;
}

.detail-dialog-header-button {
  display: flex;
}

.detail-dialog-body {

}

.sub-container {
  display: flex;
  flex-direction: column;
  align-items: start;
}

.sub-title {
  font-size: 16px;
  text-align: left;
  font-weight: bold;
  color: #333333;
}

.sub-content-wrap {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0 32px;
}

.sub-content-item {
  display: flex;
  padding-right: 32px;
}

.sub-content-item.one-third {
  width: calc(100% / 3);
}

input,
select {
  flex: 1;
  margin-left: 16px;
  border-bottom: 1px solid #0c98fe;
  color: #1867c0;
  outline: none;
}

textarea {
  width: 100%;
  height: 100px;
  border: 1px solid #d0d0d0;
  resize: none;
}

table {
  width: 100%;
  border-spacing: 0;
  border-collapse: separate;
  border-left: 1px solid #d0d0d0;
  border-top: 1px solid #d0d0d0;
}

table thead tr th {
  background: #0c98fe;
  padding: 2px;
  color: white;
  border-right: 1px solid #d0d0d0;
  border-bottom: 1px solid #d0d0d0;
}

table tbody tr:nth-child(2n) {
  background: #f4f4f4;
}

table tbody tr td {
  padding: 8px 4px;
  border-right: 1px solid #d0d0d0;
  border-bottom: 1px solid #d0d0d0;
}

table tbody tr td input {
  width: 120px;
}

table tbody tr td:nth-child(1) {
  text-align: right;
}


/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>